import React, { Component, createRef } from "react";
import styled from "styled-components";
import Sticky from "sticky-js";
import { debounce } from "lodash";
import smoothscroll from "smoothscroll-polyfill";
import { color30, color50, colorButton, colorMain, mainBg } from "../styles";

const scrollHide = 1130;
const mobile = 720;

const Wrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
  transition: opacity 0.3s ease-in-out;
  transition-delay: 0.2s;

  &.hidden {
    opacity: 0;
    transition-delay: 0s;
    transition-duration: 0.2s;
  }

  @media (max-width: ${scrollHide}px) {
    position: fixed;
    transform: translateX(-250px);
    top: 15% !important;
    left: 0;
    transition: 0.4s transform;
    background-color: ${mainBg};
    border-radius: 8px;
    padding: 8px 0;
    width: 250px !important;
    box-shadow: 0px 0px 1.15608px rgba(0, 0, 0, 0.0167767),
      0px 0px 2.6267px rgba(0, 0, 0, 0.0243888),
      0px 0px 4.57166px rgba(0, 0, 0, 0.0300374),
      0px 0px 7.2625px rgba(0, 0, 0, 0.035),
      0px 0px 11.2055px rgba(0, 0, 0, 0.0399626),
      0px 0px 17.4853px rgba(0, 0, 0, 0.0456112),
      0px 0px 29.0283px rgba(0, 0, 0, 0.0532233),
      0px 0px 58px rgba(0, 0, 0, 0.07);

    &:hover,
    &.show {
      transform: translateX(0%);
    }

    &.hidden {
      opacity: 1;
    }
  }

  @media (max-width: ${mobile}px) {
    position: static !important;
    transform: none;
    margin: 24px auto 24px;
    box-shadow: none;
    border: 1px solid ${color30};
    padding: 8px;
    transition: none !important;
    width: 100% !important;
  }
`;

const Div = styled.div`
  max-width: 243px;
  width: 18vw;

  @media (max-width: 1130px) {
    width: 250px;
    max-width: 250px;
  }

  @media (max-width: ${mobile}px) {
    width: 100%;
    max-width: 100%;
  }
`;

const Handle = styled.div`
  display: none;
  position: absolute;
  top: 50%;
  right: -30px;
  width: 30px;
  height: 30px;
  border-radius: 0 3px 3px 0;
  cursor: pointer;
  background: url("/assets/icon-toc.svg") 50% 50% no-repeat ${colorButton};
  background-size: 80% 80%;

  @media (max-width: 1130px) {
    display: inline-block;
  }

  @media (max-width: ${mobile}px) {
    display: none;
  }
`;

const Top = styled.div`
  padding-left: 36px;
  padding-bottom: 5px;
`;

const Subtitle = styled.span`
  display: block;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 171%;
  letter-spacing: -0.006em;
  color: ${color50};
`;

const Title = styled(Subtitle)`
  font-weight: bold;
  margin-top: 2px;
  position: relative;

  &:before,
  &:after {
    content: "";
    position: absolute;
    top: 11px;
    height: 100%;
    left: -22px;
    width: 3px;
    background: #908d88;
  }

  &:after {
    left: -31px;
    top: 6px;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    transition: 0.2s background-color;
  }
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  overflow: hidden;
`;

const Item = styled.a`
  display: inline-block;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 171%;
  letter-spacing: -0.006em;
  color: ${color50};
  transition: color 0.2s;
`;

const ListItem = styled.li`
  padding: 8px 0 8px 36px;
  position: relative;
  overflow: hidden;

  &:before,
  &:after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    height: 100%;
    left: 14px;
    width: 3px;
    background: #908d88;
  }

  &:after {
    left: 8px;
    top: 16px;
    width: 15px;
    height: 15px;
    border-radius: 50%;
    transition: 0.2s background-color;
  }

  &:last-of-type {
    &:before {
      top: -55%;
    }
  }

  &.current,
  &:hover {
    ${Item} {
      color: ${colorMain};
    }

    &:after {
      background-color: #333;
    }
  }
`;

class TableOfContents extends Component {
  constructor(props) {
    super(props);

    this.scrollTitles = this.scrollTitles.bind(this);
    this.overlapTable = this.overlapTable.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.removeOverLapEvent = this.removeOverLapEvent.bind(this);
    this.removeTitlesEvent = this.removeTitlesEvent.bind(this);
    this.initScrollEvent = this.initScrollEvent.bind(this);

    this.Ref = createRef();
  }

  state = {
    headings: [],
    sticky: null,
    headingsNodes: null,
    imagesNodes: null,
    oldPosition: 0,
    overLap: false,
    initOverlap: false,
    initTitles: false
  };

  componentDidMount() {
    this.getHeadings();
    if (typeof window === "object") {
      smoothscroll.polyfill();
      this.initListeners();
    }
  }

  initListeners() {
    const postContent = document.querySelector(".post-content");
    if (!postContent) {
      return;
    }

    const headings = postContent.querySelectorAll("h1,h2,h3,h4,h5,h6");
    const images = postContent.querySelectorAll(
      ".gatsby-resp-image-wrapper, img"
    );

    if (!headings) {
      return;
    }

    window.addEventListener("resize", this.handleResize, {
      passive: true
    });

    const vw = this.viewPortWidth();
    let sticky = null;

    if (vw > scrollHide) {
      this.initScrollEvent(false, this.overlapTable, {
        initOverlap: true
      });
    }

    if (vw > mobile) {
      this.initScrollEvent(false, this.scrollTitles, {
        initTitles: true
      });

      sticky = new Sticky(".stickyDiv");
    }

    this.setState({
      headingsNodes: headings,
      imagesNodes: images,
      sticky: sticky
    });
  }

  handleResize() {
    const run = debounce(() => {
      const vw = this.viewPortWidth();
      const { initOverlap, initTitles, sticky } = this.state;

      // Removes or adds the overLap event.
      if (vw > scrollHide) {
        this.initScrollEvent(initOverlap, this.overlapTable, {
          initOverlap: true
        });

        if (!sticky) {
          this.setState({
            sticky: new Sticky(".stickyDiv")
          });
        }
      } else if (vw <= scrollHide) {
        this.removeOverLapEvent(initOverlap, { initOverlap: false });

        if (sticky) {
          sticky.destroy();
          this.setState({
            sticky: null
          });
        }
      }

      if (vw > mobile) {
        this.initScrollEvent(initTitles, this.scrollTitles, {
          initTitles: true
        });
      } else if (vw <= mobile) {
        this.removeOverLapEvent(initTitles, { initTitles: false });
      }
    }, 100);

    run();
  }

  initScrollEvent(state, func, update = false) {
    if (!state) {
      document.addEventListener("scroll", func, {
        passive: true
      });

      if (update) {
        this.setState(update);
      }
    }
  }

  componentWillUnmount() {
    const { initOverlap, initTitles, sticky } = this.state;
    if (sticky) {
      sticky.destroy();
    }

    if (typeof window === "object") {
      window.removeEventListener("resize", this.handleResize, {
        passive: true
      });

      this.removeTitlesEvent(initTitles);
      this.removeOverLapEvent(initOverlap);
    }
  }

  removeTitlesEvent(state, ignoreState = false) {
    if (state) {
      document.removeEventListener("scroll", this.scrollTitles, {
        passive: true
      });

      if (ignoreState !== false) {
        this.setState(ignoreState);
      }
    }
  }

  removeOverLapEvent(state, ignoreState = false) {
    if (state) {
      document.removeEventListener("scroll", this.overlapTable, {
        passive: true
      });

      if (ignoreState !== false) {
        this.setState(ignoreState);
      }
    }
  }

  viewPortWidth() {
    return Math.max(
      document.documentElement.clientWidth || 0,
      window.innerWidth || 0
    );
  }

  viewportPercentage(elem) {
    const vh = Math.max(
      document.documentElement.clientHeight || 0,
      window.innerHeight || 0
    );
    return (elem.getBoundingClientRect().bottom - 30) / vh;
  }

  overlapTable() {
    const { imagesNodes } = this.state;
    if (!this.Ref.current) {
      return;
    }

    const vw = this.viewPortWidth();

    if (vw <= 1000) {
      return;
    }

    const table = this.Ref.current.getBoundingClientRect();
    let overLap = 0;
    let offset = 100;

    for (let i = 0; i < imagesNodes.length; i++) {
      let image = imagesNodes[i].getBoundingClientRect();
      overLap += !(
        table.right < image.left ||
        // table.left > image.right ||
        table.bottom < image.top - offset ||
        table.top - offset > image.bottom
      )
        ? 1
        : 0;
    }

    if (overLap > 0) {
      this.Ref.current.classList.add("hidden");
    } else {
      this.Ref.current.classList.remove("hidden");
    }
  }

  scrollTitles(scroll = false) {
    const run = debounce(() => {
      const { oldPosition, headingsNodes } = this.state;
      const vw = Math.max(
        document.documentElement.clientWidth || 0,
        window.innerWidth || 0
      );

      if (vw <= 1000) {
        return;
      }

      let headingsArray;
      if (oldPosition < document.documentElement.scrollTop) {
        headingsArray = headingsNodes;
      } else {
        headingsArray = [...headingsNodes].reverse();
      }

      for (let i = 0; i < headingsArray.length; i++) {
        let position = this.viewportPercentage(headingsArray[i]);
        if (position > 0.1 && position < 0.6) {
          const id = headingsArray[i].getAttribute("id");

          const active = document.querySelector(
            `.section-link.current:not([data-link="${id}"])`
          );
          const target = document.querySelector(`[data-link="${id}"]`);

          if (active) {
            active.classList.remove("current");
          }

          if (target && !target.classList.contains("current")) {
            target.classList.add("current");
          }
        }
      }

      this.setState({
        oldPosition: document.documentElement.scrollTop
      });
    }, 50);

    run();
  }

  scrollTo(event, element) {
    event.preventDefault();
    if (!element || !element.href) {
      return;
    }

    let targetID = element.getAttribute("href");
    targetID = targetID.replace("#", "");
    let targetAnchor = document.getElementById(targetID);

    if (!targetAnchor) {
      return;
    }

    const distanceToMove = this.elementDistanceToTop(targetAnchor);
    window.requestAnimationFrame(function () {
      window.scrollBy({ top: distanceToMove, left: 0, behavior: "smooth" });
    });
  }

  elementDistanceToTop(element) {
    let offset = 80;
    let viewportWidth = this.viewPortWidth();
    // Mobile spacing for the header ( from viewport )
    if (viewportWidth <= 748) {
      offset = 100;
    }

    return Math.floor(element.getBoundingClientRect().top) - offset;
  }

  getHeadings() {
    let postContent = document.querySelector(".post-content");
    if (!postContent) {
      return "";
    }

    postContent = postContent.innerHTML;

    let headingsRaw;
    let headingsArr = [];
    if (postContent && postContent.length > 1) {
      headingsRaw = postContent.match(/(<h[2-6](.*?))>(.*)(<\/h[2-6]>)/gi);
      if (headingsRaw) {
        // eslint-disable-next-line
        headingsRaw.map((item) => {
          let id = item.match(/id="([^"]*?)"/);
          let title = item.match(/>(.*?)<\//);

          if (id && id[1] && title && title[1]) {
            title = title[1].replace(">", "");
            title = title.replace("<", "");
            title = title.replace("strong", "");
            headingsArr.push({
              id: id[1],
              title: title
            });
          }
        });
      }
    }
    this.setState({ headings: headingsArr });
  }

  render() {
    const { headings } = this.state;

    if (headings.length < 1) {
      return "";
    }

    let className = "stickyDiv";

    return (
      <Wrapper ref={this.Ref} className={className} data-margin-top={64}>
        <Div>
          <Handle />
          <Top>
            <Subtitle>Table of Contents</Subtitle>
            <Title>{this.props.title}</Title>
          </Top>
          <List>
            {headings.map((item) => (
              <ListItem
                key={item.id}
                className="section-link"
                data-link={item.id}
              >
                <Item
                  onClick={(event) => this.scrollTo(event, event.currentTarget)}
                  href={`#${item.id}`}
                >
                  {item.title}
                </Item>
              </ListItem>
            ))}
          </List>
        </Div>
      </Wrapper>
    );
  }
}

export default TableOfContents;
